import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Button, message, Tag, Tooltip, Form, Col, Select, Input, Space } from 'antd';
import { EyeOutlined, AppstoreOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { 
  StoreDisplayGroup, 
  StoreDisplayGroupFilters, 
  PaginationParams 
} from '../../../types/storeDisplayGroup';
import StoreDisplayGroupService from '../../../services/storeDisplayGroupService';
import { warehouseService } from '../../../services/warehouseService';
import HQDisplayGroupService from '../../../services/hqDisplayGroupService';
import FilterPanel from '../../../components/common/FilterPanel';
import  EmptyState from '../../../components/common/EmptyState';
import NetworkError from '../../../components/common/NetworkError';
import { handleApiResponse, extractPaginatedData } from '../../../utils/responseTransformer';
import { isRetryableError, sanitizeErrorForDisplay } from '../../../utils/errorHandler';
import { formatBindedShelfCount } from '../../../utils/validation';
import dayjs from 'dayjs';

export const PAGE_SIZES = [10, 20, 50];
export const DEFAULT_PAGE_SIZE = 10;

const StoreDisplayGroupList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  
  // State management
  const [data, setData] = useState<StoreDisplayGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [storeOptionsLoading, setStoreOptionsLoading] = useState(false);
  const [storeOptions, setStoreOptions] = useState<any[]>([]);
  const [hqDisplayGroupOptions, setHqDisplayGroupOptions] = useState<{code: string, name: string}[]>([]);
  const [hqDisplayGroupOptionsLoading, setHqDisplayGroupOptionsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  
  // Filter and pagination state
  const [filters, setFilters] = useState<StoreDisplayGroupFilters>({});
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    sortBy: 'updateTime',
    sortOrder: 'desc'
  });

  // Load store options for filter dropdown
  const loadStoreOptions = useCallback(async (searchValue = '') => {
    setStoreOptionsLoading(true);
    try {
      const response = await warehouseService.listWarehouse({searchStr:searchValue});
      if (response.success && response.data) {
        setStoreOptions(response.data);
      }
    } catch (error) {
      console.error('获取门店选项失败:', error);
    } finally {
      setStoreOptionsLoading(false);
    }
  }, []);

  // Load HQ Display Group options for filter dropdown
  const loadHqDisplayGroupOptions = useCallback(async () => {
    setHqDisplayGroupOptionsLoading(true);
    try {
      const response = await HQDisplayGroupService.getHQDisplayGroups(
        { status: 'ENABLE' },
        { page: 1, pageSize: 1000 }
      );
      if (response.success && response.data) {
        setHqDisplayGroupOptions(response.data.tableData.map(group => ({
          code: group.code,
          name: group.name
        })));
      }
    } catch (error) {
      console.error('获取总部陈列分组选项失败:', error);
    } finally {
      setHqDisplayGroupOptionsLoading(false);
    }
  }, []);

  // Load store display groups data
  const loadData = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);
    
    try {
      const response = await StoreDisplayGroupService.getStoreDisplayGroups(filters, {
        page: pagination.page,
        pageSize: pagination.pageSize,
        // 移除排序参数，因为排序将在前端完成
      });
      setData(response.data.tableData);
      setTotal(response.data.paging.totalCount);

    } catch (error) {
      const errorMessage = sanitizeErrorForDisplay(error);
      setError(errorMessage);
      setData([]);
      setTotal(0);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [filters, pagination.page, pagination.pageSize]); // 移除 pagination.sortBy 和 pagination.sortOrder 依赖

  // Combined initial data load to reduce API calls
  useEffect(() => {
    // Load both store options and display group data in parallel
    const initializeData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Load datasets in parallel
        await Promise.all([
          loadStoreOptions(),
          loadHqDisplayGroupOptions(),
          loadData(false) // Pass false to prevent double loading state
        ]);
      } catch (error) {
        const errorMessage = sanitizeErrorForDisplay(error);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []); // Only run on component mount

    useEffect(() => {
      loadData(false) // Pass false to prevent double loading state
    }, [ filters, pagination]);

  // Handle filter changes
  const handleFiltersChange = (values : any) => {
    const newFilters: StoreDisplayGroupFilters = {
      storeCode: values.storeCode,
      search: values.search,
      displayGroupCode: values.displayGroupCode
    };
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
    // loadData();
  }


  // Handle reset filters
  const handleReset = () => {
    form.resetFields();
    setFilters({});
    setPagination({
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      sortBy: 'updateTime',
      sortOrder: 'desc'
    });
    // loadData();
  }

  // 使用 useMemo 实现前端排序
  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return data;
    
    // 如果没有排序字段，则返回原始数据
    if (!pagination.sortBy) return data;
    
    // 创建数据副本以避免修改原始数据
    const sorted = [...data];
    
    return sorted.sort((a, b) => {
      let aValue: any = a[pagination.sortBy as keyof StoreDisplayGroup];
      let bValue: any = b[pagination.sortBy as keyof StoreDisplayGroup];
      
      // 特殊处理日期字段
      if (pagination.sortBy === 'gmtModified') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }
      
      // 处理字符串比较
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
      }
      if (typeof bValue === 'string') {
        bValue = bValue.toLowerCase();
      }
      
      // 处理 null/undefined 值
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return pagination.sortOrder === 'asc' ? -1 : 1;
      if (bValue == null) return pagination.sortOrder === 'asc' ? 1 : -1;
      
      // 执行排序
      if (pagination.sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  }, [data, pagination.sortBy, pagination.sortOrder]);

  // Handle table pagination change
  const handleTableChange = useCallback((paginationConfig: any, _filters: any, sorter: any) => {
    const newPagination: PaginationParams = {
      page: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    };

    // Handle sorting - 仅更新排序状态，不触发后端请求
    if (sorter.field && sorter.order) {
      newPagination.sortBy = sorter.field;
      newPagination.sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
    } else {
      // 如果没有排序字段，则清除排序
      newPagination.sortBy = undefined;
      newPagination.sortOrder = undefined;
    }

    setPagination(newPagination);
  }, []);

  // Handle view detail
  const handleViewDetail = useCallback((code: string) => {
    navigate(`/store-display-groups/${code}`);
  }, [navigate]);

  // Handle planogram view
  const handlePlanogramView = useCallback((record: StoreDisplayGroup) => {
    navigate(`/store-display-groups/${record.code}/planogram`);
  }, [navigate]);



  // Handle retry for network errors
  const handleRetry = useCallback(() => {
    loadData();
  }, [loadData]);

  // Format datetime display
    const formatDateTime = (timestamp: number): string => {
      return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
    };

  // Table columns definition
  const columns: ColumnsType<StoreDisplayGroup> = [
    {
      title: t('storeDisplayGroup.shop'),
      dataIndex: 'storeCode',
      key: 'storeCode',
      render: (storeCode: string, record: StoreDisplayGroup) => (
        <Tooltip title={record.storeName}>
          {storeCode}
        </Tooltip>
      ),
      // sorter: true,
      // sortOrder: pagination.sortBy === 'storeCode' ? (pagination.sortOrder === 'asc' ? 'ascend' : 'descend') : null,
    },
    {
      title: t('storeDisplayGroup.code'),
      dataIndex: 'code',
      key: 'code',
      // sorter: true,
      // sortOrder: pagination.sortBy === 'code' ? (pagination.sortOrder === 'asc' ? 'ascend' : 'descend') : null,
    },
    {
      title: t('storeDisplayGroup.displayGroupCode'),
      dataIndex: 'displayGroupCode',
      key: 'displayGroupCode',
      // sorter: true,
      // sortOrder: pagination.sortBy === 'displayGroupCode' ? (pagination.sortOrder === 'asc' ? 'ascend' : 'descend') : null,
    },
    {
      title: t('storeDisplayGroup.name'),
      dataIndex: 'name',
      key: 'name',
      // sorter: true,
      // sortOrder: pagination.sortBy === 'name' ? (pagination.sortOrder === 'asc' ? 'ascend' : 'descend') : null,
    },
    {
      title: t('storeDisplayGroup.shopGroup'),
      dataIndex: 'shopGroupCode',
      key: 'shopGroupCode',
      render: (shopGroupCode: string, record: StoreDisplayGroup) => (
        <Tooltip title={record.shopGroupName}>
          {shopGroupCode}
        </Tooltip>
      ),
    },
    {
      title: t('storeDisplayGroup.bindedShelfCount'),
      dataIndex: 'shelfCount',
      key: 'bindedShelfCount',
      render: (shelfCount: number) => formatBindedShelfCount(shelfCount),
    },
    {
      title: t('storeDisplayGroup.updateTime'),
      dataIndex: 'gmtModified',
      key: 'gmtModified',
      render: (gmtModified: number) => formatDateTime(gmtModified),
      // sorter: true,
      // sortOrder: pagination.sortBy === 'gmtModified' ? (pagination.sortOrder === 'asc' ? 'ascend' : 'descend') : null,
    },
    {
      title: t('storeDisplayGroup.actions'),
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t('common.view')}>
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record.code)}
            />
          </Tooltip>
          <Tooltip title={t('storeDisplayGroup.planogramViewTooltip')}>
            <Button
              type="text"
              size="small"
              icon={<AppstoreOutlined />}
              onClick={() => handlePlanogramView(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Render error state
  if (error && isRetryableError({ message: error })) {
    return (
      <div className="page-container">
        <NetworkError onRetry={handleRetry} message={error} />
      </div>
    );
  }

  return (
    <div>
      <FilterPanel
        form={form}
        onFilter={handleFiltersChange}
        onReset={handleReset}
        title={t('menu.storeDisplayGroup')}
      >
        <Col span={8}>
          <Form.Item name="storeCode" label={t('storeDisplayGroup.filter.shop')}>
            <Select
              placeholder={t('storeDisplayGroup.filter.shopPlaceholder')}
              allowClear
              loading={storeOptionsLoading}
              showSearch
              filterOption={(input, option) => {
                const optionText = option?.children?.toString() || '';
                return optionText.toLowerCase().includes(input.toLowerCase());
              }}
            >
              {storeOptions.map(store => (
                <Select.Option key={store.code} value={store.code}>
                  {store.code} - {store.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="displayGroupCode" label={t('storeDisplayGroup.filter.displayGroup')}>
            <Select
              placeholder={t('storeDisplayGroup.filter.displayGroupPlaceholder')}
              allowClear
              loading={hqDisplayGroupOptionsLoading}
              showSearch
              filterOption={(input, option) => {
                const optionText = option?.children?.toString() || '';
                return optionText.toLowerCase().includes(input.toLowerCase());
              }}
            >
              {hqDisplayGroupOptions.map(group => (
                <Select.Option key={group.code} value={group.code}>
                  {group.code} - {group.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="search" label={t('storeDisplayGroup.filter.search')}>
            <Input placeholder={t('storeDisplayGroup.filter.searchPlaceholder')} />
          </Form.Item>
        </Col>
      </FilterPanel>

      <Table
        columns={columns}
        dataSource={sortedData} // 使用前端排序后的数据
        rowKey="code"
        loading={loading}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => t('storeDisplayGroup.pagination.total', { start: range[0], end: range[1], total }),
          pageSizeOptions: PAGE_SIZES.map(size => size.toString()),
        }}
        onChange={handleTableChange}
        scroll={{ x: 1400, y: 500 }}
        locale={{
          emptyText: data.length === 0 && !loading ? (
            <EmptyState 
              type={Object.keys(filters).some(key => filters[key as keyof StoreDisplayGroupFilters]) ? 'filter' : 'default'}
              onAction={Object.keys(filters).some(key => filters[key as keyof StoreDisplayGroupFilters]) ? handleReset : undefined}
              actionText={Object.keys(filters).some(key => filters[key as keyof StoreDisplayGroupFilters]) ? t('common.reset') : undefined}
            />
          ) : undefined
        }}
      />
    </div>
  );
};

export default StoreDisplayGroupList;